import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useBreakpointValue, usePrefersReducedMotion } from '@chakra-ui/react';
import Spline from '@splinetool/react-spline';

export type SplineBackgroundProps = {
  scene: string;
  prewarmMargin?: string; // e.g., '200px'
  unmountAfterMs?: number | null; // when null/0, do not unmount automatically
  disableViewportCheck?: boolean; // when true, render if other gates pass without IO visibility
  minBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'; // default 'lg' (1280px)
  disableIdle?: boolean; // when true, skip requestIdleCallback and mark ready immediately
  force?: boolean; // when true, bypass width and accessibility gates
  style?: React.CSSProperties;
};

export const SplineBackground: React.FC<SplineBackgroundProps> = ({
  scene,
  prewarmMargin = '200px',
  unmountAfterMs = null,
  disableViewportCheck = false,
  minBreakpoint = 'lg',
  disableIdle = false,
  force = false,
  style,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(disableViewportCheck);
  const [ready, setReady] = useState(disableIdle);
  const [expired, setExpired] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const timerRef = useRef<number | null>(null);

  const isDesktop = useBreakpointValue({
    base: false,
    [minBreakpoint]: true,
  });
  const prefersReducedMotion = usePrefersReducedMotion();

  const saveData = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    // @ts-ignore - not all browsers expose connection
    return navigator.connection?.saveData === true;
  }, []);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (disableViewportCheck) return;
    if (!hostRef.current) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: prewarmMargin });
    io.observe(hostRef.current);
    return () => io.disconnect();
  }, [prewarmMargin, disableViewportCheck]);

  useEffect(() => {
    if (disableIdle) return;
    if (typeof window === 'undefined') return;

    const ric = (window as any).requestIdleCallback || ((cb: Function) => setTimeout(cb, 1));
    const id = ric(() => setReady(true), { timeout: 1500 });
    return () => {
      if ((window as any).cancelIdleCallback) (window as any).cancelIdleCallback(id);
    };
  }, [disableIdle]);

  useEffect(() => {
    if (!visible || timerRef.current) return;
    if (!unmountAfterMs || unmountAfterMs <= 0) return;
    if (typeof window === 'undefined') return;

    timerRef.current = window.setTimeout(() => setExpired(true), unmountAfterMs);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [visible, unmountAfterMs]);

  const gatingOk = force || (isDesktop && !prefersReducedMotion && !saveData);
  const shouldRender = isClient && ready && visible && !expired && gatingOk;

  return (
    <Box
      ref={hostRef}
      style={{ width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden', ...style }}
      aria-hidden
    >
      {shouldRender ? <Spline scene={scene} style={{ pointerEvents: 'none' }} /> : null}
    </Box>
  );
};
